// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Help.js
// Help (/help): one short article per feature; features link here with /help#<id> (the "?" buttons).
// Add an article to ARTICLES and link to it from the feature.
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';

const ARTICLES = [
  {
    id: 'live-candles', title: 'Live (purple) candles',
    body: (
      <>
        <p>While a market is open, the chart shows today's candle so far after the last confirmed day. It is
          <b style={{ color: 'var(--live)' }}> purple</b> because it isn't final: it keeps changing until the market closes and our
          end-of-day data for that day arrives. Then the purple candle is replaced by the confirmed daily candle.
          A hollow (white) purple candle is up on the day so far; a solid purple candle is down.</p>
        <ul>
          <li><b>US stocks</b> - real-time prices. Before the open (pre-market, 4:00-9:30am New York) the candle is
            black and white and shows pre-market trading only. From the open it shows the regular session in purple.
            After the close it stays purple, and the after-hours price is shown as a grey dashed line.</li>
          <li><b>ASX stocks</b> - 20 minutes delayed, as published by the ASX. While the chart is open and in focus your
            browser refreshes it every 3 minutes; otherwise it is refreshed on our side every 20 minutes.</li>
          <li>The tag at the end of the ticker box shows the market state: PRE (US pre-market), OPEN, POST (US after
            hours) or CLOSED (closed, end-of-day data not in yet). Tap it to open this article.</li>
          <li>Indicators include the purple candle, so they move with it during the day. The black-and-white pre-market
            candle is left out of indicators and only shows on the daily timeframe.</li>
          <li>If the market has closed and the candle is still purple, our end-of-day data for that stock hasn't
            arrived yet - it will replace the purple candle when it does.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'alert-lines', title: 'Alert lines - when do they fire?',
    body: (
      <>
        <p>An alert line runs from point A to point B and is measured in candles. Each candle has its own value on the
          line; the alert compares that candle with the line's value on it. Once candles pass point B the line expires.</p>
        <ul>
          <li><b>Touch</b> alerts are checked live while the market is open, against today's candle so far - including
            US pre-market and after-hours trading. A brief move through the line between checks still counts, because the
            day's high and low are used, not just the last price. US checks run every minute (real-time prices); ASX checks
            every 20 minutes (prices are 20 minutes delayed), so an ASX touch can reach you up to about 40 minutes later.</li>
          <li><b>Close</b> alerts are only checked once the day's candle is final, after the market closes and our
            end-of-day data arrives.</li>
          <li><b>After firing</b>: <i>once</i> stops the line; <i>daily</i> fires on every candle that stays past the line;
            <i>re-arm</i> fires again only after a candle back on the other side.</li>
          <li>Alerts appear in the bell at the top of every page and in the #custom-alerts Discord channel. An alert
            already sent live is never sent again at the end of the day.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'futures', title: 'Commodity futures on the chart',
    body: (
      <>
        <p>Commodities are futures contracts. Type a commodity or its code in the chart search (for example gold, GC,
          oil). Tickers follow TradingView: <code>GC1!:COMEX</code> is the front month (the nearest contract that hasn't
          expired), <code>GC2!:COMEX</code> the second month, and <code>GCZ2026:COMEX</code> one contract (December 2026).
          The front month switches to the next contract the day after the current one expires.</p>
        <ul>
          <li><b>ADJ</b> (back-adjust) removes the price jump at each contract switch by shifting earlier candles by the
            gap between the two contracts. Off = the real prices traded at the time.</li>
          <li><b>SET</b> uses the exchange's official settlement price as the daily close (TradingView's default).
            Settlements are collected from October 2026; older candles use the last trade.</li>
          <li><b>EXT</b> (needs ADJ) adds a longer history, back to 2019, before our own contract data starts (about two
            years). That part is a ratio-adjusted series, so its shape and % moves are right but its price levels are
            relative to today's contract.</li>
          <li>Ratios work with futures too, for example <code>NST:AU/GC1!:COMEX</code> (a gold miner against gold).</li>
          <li>Prices are delayed and updated once a day after the US close. Lithium and uranium aren't available.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'account-security', title: 'Account security - logins, new locations, protection mode',
    body: (
      <>
        <p>chart.ninja watches for break-in attempts and reacts on its own. Here is what you may notice as a user and why.</p>
        <ul>
          <li><b>Repeated wrong passwords</b> from one internet address block that address for a while (the block gets
            longer each time it happens again). If you share an office or home connection with other members, people who
            are already logged in keep working; only new logins from that address are held until the block ends.</li>
          <li><b>Protection mode.</b> If someone tries many passwords on <i>your</i> account from several different places,
            your account goes into protection mode for a day and you get an email about it. During that time you can only
            log in from internet addresses you have used before.</li>
          <li><b>Logging in from a new place while protected.</b> You will see the normal "invalid credentials" message
            even with the right password - that is deliberate, so an attacker cannot tell when they have guessed it. Check
            your email: there is a link to approve the new address (valid for an hour). Approve it only if it was you,
            then log in again. If it was not you, do not click the link - someone has your password - and change it from
            the login page.</li>
          <li><b>Strict mode.</b> You can ask the admin to make protection mode permanent for your account, so logins are
            always limited to known addresses plus the email approval.</li>
          <li><b>Signing up.</b> Some email providers and addresses are not accepted, and registration may be by
            invitation only at times.</li>
          <li><b>Logged out unexpectedly?</b> Changing your password logs you out everywhere else, and the admin can end
            all sessions of an account. Just log in again.</li>
        </ul>
        <p>Nothing here changes how you normally use the site: a login from a place you have used before, with the right
          password, always works.</p>
      </>
    ),
  },
];

const S = {
  page:  { minHeight: '100vh', background: 'var(--bg)' },
  wrap:  { maxWidth: '820px', margin: '0 auto', padding: '24px 16px 64px' },
  h1:    { fontSize: '22px', margin: '0 0 6px', color: 'var(--text)' },
  intro: { color: 'var(--text-muted)', fontSize: '14px', margin: '0 0 20px' },
  toc:   { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' },
  tocA:  { fontSize: '13px', color: 'var(--accent)', textDecoration: 'none', border: '1px solid var(--border)', borderRadius: '999px', padding: '4px 10px', background: 'var(--surface)' },
  art:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px', marginBottom: '16px', fontSize: '14px', lineHeight: 1.55, color: 'var(--text)', scrollMarginTop: '16px' },
  h2:    { fontSize: '17px', margin: '0 0 8px' },
};

export default function Help() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
  }, [hash]);
  return (
    <div style={S.page}>
      <Navbar />
      <div style={S.wrap}>
        <h1 style={S.h1}>Help</h1>
        <p style={S.intro}>How chart.ninja's features behave. The "?" buttons around the site open the matching article.</p>
        <div style={S.toc}>{ARTICLES.map(a => <a key={a.id} href={`#${a.id}`} style={S.tocA}>{a.title}</a>)}</div>
        {ARTICLES.map(a => (
          <section key={a.id} id={a.id} style={S.art}>
            <h2 style={S.h2}>{a.title}</h2>
            {a.body}
          </section>
        ))}
      </div>
    </div>
  );
}
